import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Trash2, Send, Image as ImageIcon } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PillarBadge } from "@/constants/pillars";
import SavedCardPay from "@/components/SavedCardPay";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function OfferDetailPage() {
  const { oid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/offers/${oid}`);
      setOffer(data);
      const { data: mine } = await api.get("/payments/mine");
      const has = mine.some((p) => p.kind === "offer" && p.ref_id === oid && p.payment_status === "paid");
      setPaid(has);
      if (has) loadChat();
    } catch { toast.error("Offerta non trovata"); }
  };
  const loadChat = async () => {
    try { const { data } = await api.get(`/offer-chat/${oid}/${user.id}`); setChat(data); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [oid]);

  const buy = async (useCashback = false) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/offer/${oid}/checkout`, { origin_url: window.location.origin, use_cashback: useCashback === true });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const del = async () => {
    await api.delete(`/offers/${oid}`);
    toast.success("Offerta eliminata"); navigate("/marketplace");
  };

  const send = async () => {
    if (!msg.trim()) return;
    const { data } = await api.post(`/offer-chat/${oid}/${user.id}`, { text: msg });
    setChat([...chat, data]); setMsg("");
  };

  if (!offer) return <p className="text-center text-neutral-400 py-10 text-sm">Caricamento…</p>;
  const isOwner = offer.author_id === user?.id || user?.role === "admin";

  return (
    <div className="pb-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-neutral-500 p-4"><ArrowLeft size={18} /> Indietro</button>
      <div className="aspect-video bg-neutral-100">
        {offer.image_url ? <img src={fileUrl(offer.image_url)} alt={offer.title} className="w-full h-full object-cover" /> :
          <div className="w-full h-full flex items-center justify-center text-neutral-300"><ImageIcon size={40} /></div>}
      </div>
      <div className="p-4">
        <PillarBadge id={offer.pillar} />
        <h1 className="font-display text-2xl font-bold mt-2">{offer.title}</h1>
        <p className="text-sm text-neutral-500 mt-1">di {offer.author?.name}</p>
        <p className="text-sm mt-3 whitespace-pre-wrap leading-relaxed">{offer.description}</p>
        <div className="mt-5 flex items-center justify-between">
          <span className="font-display text-3xl font-extrabold">€{Number(offer.price).toFixed(2)}</span>
          {isOwner ? (
            <Button data-testid="delete-offer-btn" onClick={del} variant="outline" className="h-11 rounded-full border-red-200 text-red-600"><Trash2 size={16} className="mr-1" />Elimina</Button>
          ) : paid ? (
            <span className="text-emerald-600 font-semibold text-sm">Acquistato ✓</span>
          ) : (
            <SavedCardPay
              endpoint={`/payments/offer/${oid}/pay-saved`}
              amount={Number(offer.price)}
              label="Acquista"
              checkoutLabel="Acquista ora"
              onPaid={() => { setPaid(true); loadChat(); }}
              onCheckout={buy}
            />
          )}
        </div>

        {paid && (
          <div className="mt-8 border-t border-neutral-200 pt-5">
            <h2 className="font-display font-bold mb-3">Chat con il venditore</h2>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
              {chat.length === 0 && <p className="text-xs text-neutral-400">Inizia la conversazione.</p>}
              {chat.map((m) => (
                <div key={m.id} className={`flex ${m.author_id === user.id ? "justify-end" : "justify-start"}`}>
                  <span className={`text-sm rounded-2xl px-3 py-1.5 max-w-[75%] ${m.author_id === user.id ? "bg-black text-white" : "bg-neutral-100"}`}>{m.text}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Messaggio…" className="h-10" />
              <Button data-testid="chat-send" onClick={send} className="h-10 w-10 p-0 rounded-full bg-black"><Send size={16} /></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
